<template>
  <div>
    <v-app-bar flat color="transparent">
      <v-toolbar-title>
        Categories
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn @click="getAllCategories" class="ml-5" icon>
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
      <v-btn class="ml-5" color="primary" @click="saveCategories" :loading="loading">
        Save
      </v-btn>
    </v-app-bar>
    <div class="ma-4" id="category-list">
      <v-row>
        <v-col cols="4">
          <v-card>
            <v-card-title>Add Category</v-card-title>
            <v-form ref="form" class="px-5 pb-5">
              <label class="text-field-label pl-4"> Label </label>
              <v-text-field
                flat solo rounded
                required
                background-color="grey lighten-3"
                class="mt-2"
                v-model="form.label"
                :error-messages="form.$getError('label')"
              ></v-text-field>
              <div class="category-image mb-5">
                <input v-model="form.image" hidden />
                <div class="category-image-container" v-if="categoryImage">
                  <div class="category-image-delete">
                    <v-btn color="error" @click="removeImage" depressed small>
                      Remove
                    </v-btn>
                  </div>
                  <v-img
                    class="mb-4"
                    :src="categoryImage"
                    :lazy-src="categoryImage"
                    size="100%"
                  />
                </div>
                <div class="drop py-10" v-else>
                  <v-btn elevation="0" small @click="showCropperModal = !showCropperModal">Select cover image</v-btn>
                </div>
              </div>
              <v-btn
                color="primary"
                :loading="addingCategory"
                :disabled="addingCategory || !form.label"
                @click="addCategory"
              >Submit</v-btn>
            </v-form>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card>
            <v-card-title>Top Categories</v-card-title>
            <v-simple-table>
              <thead>
                <tr>
                  <th width="10%"></th>
                  <th class="text-left" width="50%">Label</th>
                  <th class="text-right">&nbsp;</th>
                </tr>
              </thead>
              <draggable :list="topCategories" group="category" tag="tbody" ghost-class="ghost">
                <tr v-for="(category, index) in topCategories" :key="index" class="list-draggable">
                  <td>
                    <v-avatar size="32">
                      <img :src="getImgUrl(category.cover_photo)">
                    </v-avatar>
                  </td>
                  <td>{{ category.label }}</td>
                  <td class="text-right show-hover">
                    <v-btn @click="showEditCategoryModal(category)" icon small>
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </draggable>
            </v-simple-table>
          </v-card>
        </v-col>
        <v-col cols="4">
          <v-card>
            <v-card-title>Other Categories</v-card-title>
            <v-simple-table>
              <thead>
                <tr>
                  <th width="10%"></th>
                  <th class="text-left" width="50%">Label</th>
                  <th class="text-right">&nbsp;</th>
                </tr>
              </thead>
              <draggable :list="belowCategories" group="category" tag="tbody">
                <tr v-for="(category, index) in belowCategories" :key="index" class="list-draggable">
                  <td>
                    <v-avatar size="32">
                      <img :src="getImgUrl(category.cover_photo)">
                    </v-avatar>
                  </td>
                  <td>{{ category.label }}</td>
                  <td class="text-right show-hover">
                    <v-btn @click="showEditCategoryModal(category)" icon small>
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </draggable>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <image-cropper
      :aspect-ratio="16 / 9"
      v-model="showCropperModal"
      @change="imageChanged"
    />
    <edit-category v-model="showEditCategory"></edit-category>
    <v-snackbar v-model="snackbar.show" right>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text color="green" v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Form from '@/utils/form'
import draggable from 'vuedraggable'
import { mapState, mapActions } from 'vuex'
import ImageCropper from '@/components/modals/ImageCropper'
import EditCategory from '@/components/modals/EditCategory'

export default {
  data () {
    return {
      loading: false,
      addingCategory: false,
      showCropperModal: false,
      showEditCategory: false,
      categoryImage: null,
      snackbar: {
        show: false,
        message: null
      },
      form: new Form({
        label: null,
        image: null
      }),
      noImage: require('@/assets/images/no-image.jpeg')
    }
  },

  components: {
    draggable, ImageCropper, EditCategory
  },

  computed: {
    ...mapState({
      topCategories: state => state.category.topCategories,
      belowCategories: state => state.category.belowCategories
    })
  },

  methods: {
    ...mapActions({
      getAllCategories: 'category/getAllCategories'
    }),

    saveCategories () {
      this.loading = true

      this.$api.post(`/categories/set-order`, {
        top_categories: this.topCategories,
        categories: this.belowCategories
      })
        .then((response) => {
          this.showSnackbar(response.data.message)
        })
        .catch((response) => {
          this.showSnackbar(response.data.message)
        })
        .finally(() => (this.loading = false))
    },

    addCategory () {
      this.loading = true

      const formData = new FormData()
      formData.append('label', this.form.label)

      if (this.form.image) {
        formData.append('image', this.form.image)
      }

      this.$api.post(`/categories`, formData)
        .then(() => {
          this.$refs.form.reset()
          this.form.$clearErrors()
          this.showSnackbar('Category added successfully!')
          this.getAllCategories()
        })
        .catch(err => {
          if (err.response.status === 422) {
            this.form.$setErrors(err.response.data.errors)
          }
        })
        .finally(() => (this.loading = false))
    },

    showSnackbar (message) {
      this.snackbar.message = message
      this.snackbar.show = true
    },

    imageChanged (image) {
      const reader = new FileReader()
      reader.readAsDataURL(image)
      reader.onload = e => {
        this.categoryImage = reader.result
        this.form.image = image
      }
    },

    removeImage () {
      this.categoryImage = null
      this.form.image = null
    },

    getImgUrl (coverPhoto) {
      return coverPhoto ? coverPhoto.thumb_url : this.noImage
    },

    showEditCategoryModal (category) {
      this.showEditCategory = true
      this.$store.commit('category/setCurrentCategory', category)
    }
  },

  created () {
    this.getAllCategories()
  }
}
</script>

<style scoped>
.list-draggable {
  cursor: move;
}

pre {
  text-align: start;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.drop {
  background-color: #f2f2f2;
  border: 4px dashed #ccc;
  background-color: #f6f6f6;
  border-radius: 2px;
  height: 100%;
  max-height: 400px;
  max-width: 600px;
  width: 100%;
  display: inline-block;
  vertical-align: middle;
  text-align: center;
}

input[type="file"] {
  position: absolute;
  opacity: 0;
  z-index: -1;
}

.show-hover > * {
  opacity: 0;
}

.list-draggable:hover .show-hover > * {
  opacity: 100%;
}

.category-image-container {
  position: relative;
}

.category-image-delete {
  position: absolute;
  z-index: 99;
  top: 10px;
  right: 10px;
}
</style>
